import styleVariables from '@/styles/style.module.scss';
import BastTab from '@/components/ui/BaseTab';
import { useRegistrationState } from '@/provider/registration/registrationContext';
import {
    ApLockInfo,
    DEVICE_STATUS,
    DEVICE_TYPE,
    InfraDeviceRow,
    RegisterAPAndRTRequest,
    REGISTRATION_MODE,
    TAB_TYPE,
    TabType,
} from '@/types/pages/storeManagement/registration/registrationTypes';
import { Add, Edit, LockOpenOutlined, LockOutlined, Refresh, Sensors } from '@mui/icons-material';
import { Box, Chip, Typography } from '@mui/material';
import { useCallback, MouseEvent, useMemo, FC } from 'react';
import BaseButton from '../ui/BaseButton';
import BaseDataTable from '../ui/BaseDataTable';
import {
    GRID_CHECKBOX_SELECTION_COL_DEF,
    GridColDef,
    GridPaginationModel,
    GridRenderCellParams,
    GridRowSelectionModel,
    GridSortModel,
    useGridApiRef,
} from '@mui/x-data-grid';
import { registerAPAndRTAPI } from '@/api/storeManagement/registration';
import BaseIconButton from '../ui/BaseIconButton';
import { useAppContext } from '@/provider/contexts';
import { useRegistrationButtonState } from '@/hooks/registration/useRegistrationButtonState';
import { useTranslation } from 'react-i18next';
import BaseAlert from '../feedback/BaseAlert';
import BaseTableColumn from '../ui/BaseTableColumn';
import TagProgressBar from './tagProgress/TagProgressBar';
import { useRegistrationLifecycleState } from '@/provider/registration/registrationLifecycleContext';

const DeviceTab: FC = () => {
    const { t } = useTranslation('common');
    const { manageStoreId, setNotification } = useAppContext();
    const { state, dispatch } = useRegistrationState();
    const buttonState = useRegistrationButtonState();
    const { formatUtcToStoreTime, refreshRegistrationNow, refreshTable } = useRegistrationLifecycleState();
    const apiRef = useGridApiRef();

    const rows: InfraDeviceRow[] = useMemo(() => {
        const success = styleVariables.successColor;
        const error = styleVariables.errorColor;

        return state.deviceInfoState.infraDeviceInfo.map((row) => {
            const isAP = row.device_type_id === DEVICE_TYPE.AP;
            const isRT = row.device_type_id === DEVICE_TYPE.RT;
            const isOnline = row.ed_status === 'ONLINE';
            const isLocked = row.network_mode === 'LOCK';

            const connectionLabel = isOnline ? 'ONLINE' : 'OFFLINE';
            const accessLabel = isAP ? (isLocked ? '【 LOCKED 】' : '【 UNLOCKED 】') : '';
            const edStatusDisplay = accessLabel ? `${connectionLabel} ${accessLabel}` : connectionLabel;
            const edStatusColor = isOnline ? success : error;

            const isRTUnconfirmed = isRT && row.confirmed === DEVICE_STATUS.UN_CONFIRMED;
            const edStatusLocalTime = formatUtcToStoreTime(row.ed_status_time);

            return {
                ...row,
                isAP,
                isRT,
                isOnline,
                isLocked,
                edStatusDisplay,
                edStatusColor,
                isRTUnconfirmed,
                edStatusLocalTime,
            };
        });
    }, [state.deviceInfoState.infraDeviceInfo, formatUtcToStoreTime]);

    const parentConfirmedMap = useMemo(() => {
        const map = new Map<string, boolean>();
        for (const d of state.infraDevice) {
            map.set(d.mac, d.confirmed === DEVICE_STATUS.CONFIRMED);
        }
        return map;
    }, [state.infraDevice]);

    const handleAddType = useCallback(
        (key: TabType) => () => {
            if (key === TAB_TYPE.TAG) {
                dispatch({ type: 'TOGGLE_DIALOG', payload: { dialog: 'isTagOpen', isOpen: true } });
            } else {
                dispatch({ type: 'TOGGLE_DIALOG', payload: { dialog: 'isApOpen', isOpen: true } });
            }
        },
        [dispatch],
    );

    const renderSimpleCell = useCallback((params: GridRenderCellParams<InfraDeviceRow, string | number>) => {
        const value = params.value;
        const displayValue = value === null || value === undefined || value === '' ? '-' : String(value);
        return <BaseTableColumn columnValue={displayValue} />;
    }, []);

    const renderMACColumn = useCallback(
        (params: GridRenderCellParams<InfraDeviceRow, string>) => {
            const editClickHandler = (event: MouseEvent<HTMLButtonElement>, selection: InfraDeviceRow) => {
                event.stopPropagation();
                dispatch({ type: 'TOGGLE_DIALOG', payload: { dialog: 'isApOpen', isOpen: true } });
                dispatch({ type: 'SET_AP_INFO', payload: selection });
            };

            return (
                <BaseTableColumn>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ minWidth: 0 }}>{params.value}</Box>
                        {state.currentTab === TAB_TYPE.AP && params.row.isAP && (
                            <BaseIconButton
                                color="secondary"
                                onClick={(e) => editClickHandler(e, params.row)}
                                disabled={!buttonState.editAP}
                                size="small"
                            >
                                <Edit fontSize="small" />
                            </BaseIconButton>
                        )}
                        {state.currentTab === TAB_TYPE.RT && params.row.isAP && (
                            <Chip label="AP" color="info" size="small" />
                        )}
                    </Box>
                </BaseTableColumn>
            );
        },
        [dispatch, state.currentTab, buttonState.editAP],
    );

    const renderEdStatusColumn = useCallback((params: GridRenderCellParams<InfraDeviceRow, string>) => {
        const color = params.row.edStatusColor;
        return (
            <BaseTableColumn>
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        lineHeight: 1,
                        fontWeight: 500,
                        color,
                    }}
                >
                    <span
                        style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            display: 'inline-block',
                            backgroundColor: color,
                        }}
                    />
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, lineHeight: 1 }}>
                        {params.row.edStatusDisplay}
                    </span>
                </span>
            </BaseTableColumn>
        );
    }, []);

    const renderConfirmedColumn = useCallback(
        (params: GridRenderCellParams<InfraDeviceRow, number>) => {
            const handleLockClick = (info: ApLockInfo) => {
                dispatch({ type: 'TOGGLE_DIALOG', payload: { dialog: 'isApLockOpen', isOpen: true } });
                dispatch({ type: 'SET_AP_LOCK_INFO', payload: info });
            };

            const confirmRTHandler = async (mac: string) => {
                try {
                    const postData: RegisterAPAndRTRequest = { mac, type: 'rt', store_id: manageStoreId as number };
                    const {
                        data: { isSuccess, message },
                    } = await registerAPAndRTAPI(postData);
                    setNotification({ message, severity: isSuccess ? 'success' : 'error' });
                    void refreshRegistrationNow();
                } catch {
                    setNotification({ message: t('Something went wrong'), severity: 'error' });
                }
            };
            const displayFormatted =
                params.formattedValue === undefined || params.formattedValue === null
                    ? '-'
                    : String(params.formattedValue);
            if (state.currentTab === TAB_TYPE.AP || state.currentTab === TAB_TYPE.TAG) {
                return <BaseTableColumn columnValue={displayFormatted} />;
            }
            if (params.row.isAP) {
                return (
                    <BaseTableColumn>
                        <BaseButton
                            size="small"
                            color={params.row.isLocked ? 'success' : 'error'}
                            startIcon={params.row.isLocked ? <LockOpenOutlined /> : <LockOutlined />}
                            disabled={state.deviceInfoState.doingTask}
                            loading={state.deviceInfoState.doingTask}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleLockClick({ mac: params.row.mac, isLock: params.row.isLocked });
                            }}
                        >
                            {params.row.isLocked ? t('UNLOCK') : t('LOCK')}
                        </BaseButton>
                    </BaseTableColumn>
                );
            }

            if (params.row.isRTUnconfirmed) {
                const parentConfirmed = parentConfirmedMap.get(params.row.parent_mac) ?? true;
                const shouldDisable =
                    !buttonState.confirmRT ||
                    !params.row.isOnline ||
                    !parentConfirmed ||
                    state.deviceInfoState.doingTask;

                return (
                    <BaseTableColumn>
                        <BaseButton
                            size="small"
                            color="secondary"
                            startIcon={<Sensors />}
                            disabled={shouldDisable}
                            onClick={(e) => {
                                e.stopPropagation();
                                confirmRTHandler(params.row.mac);
                            }}
                        >
                            {t('Confirm')}
                        </BaseButton>
                    </BaseTableColumn>
                );
            }

            return <BaseTableColumn columnValue={displayFormatted} />;
        },
        [
            state.currentTab,
            state.deviceInfoState.doingTask,
            dispatch,
            refreshRegistrationNow,
            manageStoreId,
            setNotification,
            parentConfirmedMap,
            buttonState.confirmRT,
            t,
        ],
    );

    const infraDeviceColumns = useMemo<GridColDef<InfraDeviceRow>[]>(
        () => [
            { ...GRID_CHECKBOX_SELECTION_COL_DEF, hideable: false },
            {
                field: 'mac',
                headerName: 'MAC',
                flex: 1,
                valueGetter: (_value, row) => row.mac, // 單一值
                renderCell: renderMACColumn,
            },
            {
                field: 'parent_mac',
                headerName: 'Parent Mac',
                flex: 1,
                renderCell: renderSimpleCell,
            },
            {
                field: 'ed_status',
                headerName: 'Device Status',
                flex: 1,
                renderCell: renderEdStatusColumn,
            },
            {
                field: 'ed_status_time',
                headerName: 'Status Time',
                flex: 1,
                valueGetter: (_value, row) => row.edStatusLocalTime,
            },
            {
                field: 'confirmed',
                headerName: 'Registration Status',
                flex: 1,
                minWidth: 140,
                sortable: state.currentTab === TAB_TYPE.TAG,
                valueGetter: (_value, row) => row.confirmed,
                valueFormatter: (value) => {
                    if (value === DEVICE_STATUS.UN_CONFIRMED) return t('Unconfirmed');
                    if (value === DEVICE_STATUS.CONFIRMED) return t('Confirmed');
                    if (value === DEVICE_STATUS.IN_PROGRESS || value === 3) return t('In Progress');
                    return '-';
                },
                renderCell: renderConfirmedColumn,
            },
        ],
        [renderMACColumn, renderEdStatusColumn, renderConfirmedColumn, renderSimpleCell, state.currentTab, t],
    );

    const rowSelectHandler = (selectionModel: GridRowSelectionModel) => {
        const selectedItems = rows.filter((row) => selectionModel.includes(row.mac));
        dispatch({
            type: 'SET_TABLE_STATE',
            payload: { selectedInfraDevices: selectedItems },
        });
    };

    const sortModelChangeHandler = (sortModel: GridSortModel) => {
        if (sortModel.length > 0) {
            const { field, sort } = sortModel[0];
            dispatch({
                type: 'SET_TABLE_STATE',
                payload: { sortModel: { field: field, sort: sort as 'asc' | 'desc' } },
            });
            void refreshTable({
                tab: state.currentTab,
                sortModel: { field, sort: sort as 'asc' | 'desc' },
                clearSelected: true,
            });
        } else {
            dispatch({
                type: 'SET_TABLE_STATE',
                payload: { sortModel: { field: null, sort: null } },
            });
            void refreshTable({
                tab: state.currentTab,
                sortModel: { field: null, sort: null },
                clearSelected: true,
            });
        }
    };

    const infraDevicesPageChangeHandler = (pageModel: GridPaginationModel) => {
        dispatch({ type: 'SET_TABLE_STATE', payload: { paginationModel: pageModel } });
        void refreshTable({
            tab: state.currentTab,
            paginationModel: pageModel,
            clearSelected: true,
        });
    };

    const handleTabOnChange = useCallback(
        (prev: string) => {
            dispatch({ type: 'SET_TAB', payload: prev as TAB_TYPE });
            void refreshTable({
                tab: prev as TAB_TYPE,
                resetTable: true,
            });
        },
        [dispatch, refreshTable],
    );
    return (
        <BastTab tab={state.currentTab} onChange={handleTabOnChange} labelOptions={state.labelOptions}>
            {state.registrationMode === REGISTRATION_MODE.NORMAL_MODE && state.infraDevice.length < 1 ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '300px',
                    }}
                >
                    <Typography variant="h4" gutterBottom>
                        {t('Please Start Installation Mode to do Registration.')}
                    </Typography>
                </Box>
            ) : (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', my: 3 }}>
                        {buttonState.delete.warning && (
                            <BaseAlert
                                sx={{ padding: '0px 20px 0px 14px' }}
                                severity="warning"
                                message={t(buttonState.delete.warning)}
                            />
                        )}
                        <Box sx={{ flex: 1, width: '100%', maxWidth: 300, mr: 2 }}>
                            {state.registrationMode === REGISTRATION_MODE.INSTALLATION_MODE &&
                                state.currentTab === TAB_TYPE.TAG &&
                                state.tagState.isOnProgress && (
                                    <TagProgressBar
                                        confirmedTags={state.tagState.status.confirmed}
                                        totalTags={state.tagState.status.total}
                                        showPercentage
                                        showCount
                                    />
                                )}
                        </Box>
                        <Box sx={{ display: 'flex' }}>
                            {state.currentTab !== TAB_TYPE.RT && (
                                <BaseButton
                                    sx={{ mr: 2 }}
                                    variant="contained"
                                    color="primary"
                                    startIcon={<Add />}
                                    disabled={
                                        state.currentTab === TAB_TYPE.AP ? !buttonState.addAP : !buttonState.addTag
                                    }
                                    onClick={handleAddType(state.currentTab)}
                                >
                                    {t('Add')}
                                </BaseButton>
                            )}
                            <BaseButton
                                variant="contained"
                                color="primary"
                                startIcon={<Refresh />}
                                onClick={() => {
                                    void refreshTable({
                                        tab: state.currentTab,
                                    });
                                }}
                                disabled={!buttonState.refresh}
                            >
                                {t('Refresh')}
                            </BaseButton>
                        </Box>
                    </Box>
                    <BaseDataTable
                        apiRef={apiRef}
                        columns={infraDeviceColumns}
                        rows={rows}
                        checkboxSelection
                        paginationModel={state.deviceInfoState.paginationModel}
                        loading={state.deviceInfoState.isLoading}
                        paginationMode="server"
                        sortingMode="server"
                        isRowSelectable={(params) => !(state.currentTab === TAB_TYPE.RT && params.row.isAP)}
                        rowCount={state.deviceInfoState.rowCount || 0}
                        onRowSelectionModelChange={rowSelectHandler}
                        onSortModelChange={sortModelChangeHandler}
                        onPaginationModelChange={infraDevicesPageChangeHandler}
                        getRowId={(row) => row.mac}
                        localeText={{ noRowsLabel: t('No devices') }} // 若要多語
                        getRowClassName={(param) =>
                            state.currentTab === TAB_TYPE.RT && param.row.isAP ? 'ap-row' : ''
                        }
                    />
                </>
            )}
        </BastTab>
    );
};
export default DeviceTab;
